import { useEffect, useMemo, useState } from "react";
import { useLang } from "../../i18n";

export const UNITS = {
  T1: {
    id: "T1",
    surface: "18 m²",
    capacity: 2,
    computeNightly: () => 90,
  },
  T2: {
    id: "T2",
    surface: "45 m²",
    capacity: 2,
    computeNightly: () => 120,
  },
  T3: {
    id: "T3",
    surface: "75 m²",
    capacity: 4,
    computeNightly: () => 180,
  },
  VILLA: {
    id: "VILLA",
    surface: "Villa",
    capacity: 6,
    computeNightly: () => 350,
  },
};

const UPSELLS = [
  { id: "brunch", price: 15, perPerson: true },
  { id: "laundry", price: 15, perPerson: false },
  { id: "latecheckout", price: 30, perPerson: false },
];

const WHATSAPP_URL = "https://wa.me/594694449985";

function diffNights(a, b) {
  if (!a || !b) return 0;
  const da = new Date(a);
  const db = new Date(b);
  const ms = db.getTime() - da.getTime();
  if (Number.isNaN(ms) || ms <= 0) return 0;
  return Math.round(ms / 86400000);
}

export function BookingModal({ open, onClose, initialUnit }) {
  const { t } = useLang();
  const m = t.modal;
  const [step, setStep] = useState(1);
  const [unit, setUnit] = useState(initialUnit || "T2");
  const [arrival, setArrival] = useState("");
  const [departure, setDeparture] = useState("");
  const [adults, setAdults] = useState(2);
  const [picks, setPicks] = useState({});
  const [contact, setContact] = useState({ prenom: "", nom: "", email: "", telephone: "", message: "" });
  const [errors, setErrors] = useState({});
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (open) {
      setStep(1);
      setSent(false);
      setErrors({});
      setArrival("");
      setDeparture("");
      setAdults(2);
      setPicks({});
      setContact({ prenom: "", nom: "", email: "", telephone: "", message: "" });
      if (initialUnit) setUnit(initialUnit);
    }
  }, [open, initialUnit]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  const u = UNITS[unit];
  const uName = t.units[unit];
  const nights = diffNights(arrival, departure);
  const nightly = u.computeNightly(nights);
  const extraPersons = Math.max(0, adults - u.capacity);
  const extraPersonsCost = extraPersons * 25 * nights;

  const upsellTotal = useMemo(() => {
    return UPSELLS.reduce((sum, up) => {
      if (!picks[up.id]) return sum;
      return sum + (up.perPerson ? up.price * adults : up.price);
    }, 0);
  }, [picks, adults]);

  const stayTotal = nightly * nights + extraPersonsCost;
  const taxeSejour = 1.5 * adults * nights;
  const grandTotal = stayTotal + upsellTotal + taxeSejour;

  if (!open) return null;

  function next() {
    if (step === 2 && nights <= 0) return;
    setStep((s) => Math.min(5, s + 1));
  }

  function prev() {
    setStep((s) => Math.max(1, s - 1));
  }

  // Build the WhatsApp message dynamically - always use wa.me (never api.whatsapp.com)
  const whatsappMessage = [
    m.waIntro,
    `${m.waUnit}: ${uName}`,
    arrival && departure ? m.waDates(arrival, departure, nights) : null,
    `${m.waTravelers}: ${adults}`,
    `${m.waOptions}: ${UPSELLS.filter((up) => picks[up.id]).map((up) => m.upsellLabels[up.id]).join(", ") || m.waNone}`,
    nights > 0 ? `${m.waTotal}: ${grandTotal.toFixed(2)} €` : null,
    `—`,
    `${contact.prenom} ${contact.nom}`.trim() || null,
    contact.email ? `Email: ${contact.email}` : null,
    contact.telephone ? `Tél: ${contact.telephone}` : null,
    contact.message ? `Message: ${contact.message}` : null,
  ]
    .filter(Boolean)
    .join("\n");

  const whatsappHref = `${WHATSAPP_URL}?text=${encodeURIComponent(whatsappMessage)}`;

  function validateAndSend(e) {
    // Validation only — the <a href> handles opening WhatsApp natively (no window.open, Safari-friendly)
    const newErrors = {};
    if (!contact.prenom.trim()) newErrors.prenom = m.errors.prenom;
    if (!contact.nom.trim()) newErrors.nom = m.errors.nom;
    if (!contact.email.trim() || !contact.email.includes("@")) newErrors.email = m.errors.email;
    if (!contact.telephone.trim()) newErrors.telephone = m.errors.telephone;

    if (Object.keys(newErrors).length > 0) {
      e.preventDefault();
      setErrors(newErrors);
      return;
    }

    setSent(true);
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-ink/60 px-0 pt-10 backdrop-blur-sm sm:items-center sm:px-4 sm:pt-0"
      onClick={onClose}
    >
      <div
        className="relative flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden bg-background shadow-2xl sm:rounded-sm"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-gold">{m.header}</p>
            <p className="font-display text-xl">{m.step} {Math.min(step, 5)} / 5</p>
          </div>
          <button onClick={onClose} aria-label={m.close} className="text-foreground/60 hover:text-foreground text-xl leading-none">
            ×
          </button>
        </div>

        <div className="h-px bg-border">
          <div className="h-full bg-gold transition-all" style={{ width: `${(step / 5) * 100}%` }} />
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-7">
          {step === 1 && (
            <div>
              <h3 className="font-display text-2xl">{m.s1title}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{m.s1sub}</p>
              <div className="mt-6 grid gap-3">
                {Object.values(UNITS).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setUnit(opt.id)}
                    title={`${m.priceHint} · ${m.baseLabels[opt.id]}`}
                    className={`flex items-center justify-between border p-4 text-left transition-all ${
                      unit === opt.id ? "border-gold bg-gold/5" : "border-border hover:border-foreground/30"
                    }`}
                  >
                    <div>
                      <div className="font-display text-lg">{t.units[opt.id]}</div>
                      <div className="text-xs text-muted-foreground">
                        {opt.surface} · {m.upTo} {opt.capacity} {m.pers}
                      </div>
                    </div>
                    {unit === opt.id && (
                      <div className="text-[10px] uppercase tracking-[0.18em] text-gold">{m.baseLabels[opt.id]}</div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <h3 className="font-display text-2xl">{m.s2title}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{m.s2sub}</p>
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <FieldDate label={m.arrival} value={arrival} onChange={setArrival} />
                <FieldDate label={m.departure} value={departure} onChange={setDeparture} min={arrival} />
              </div>
              <div className="mt-6 border border-border p-4 text-sm">
                <Row label={m.nightsLabel}>
                  <strong>{nights || "—"}</strong>
                </Row>
                <Row label={m.rateLabel}>
                  <strong>{nights ? `${nightly} ${m.perNight}` : "—"}</strong>
                </Row>
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <h3 className="font-display text-2xl">{m.s3title}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{m.s3sub(u.capacity)}</p>
              <div className="mt-6 flex items-center gap-4">
                <button
                  type="button"
                  onClick={() => setAdults(Math.max(1, adults - 1))}
                  className="h-10 w-10 border border-border text-lg"
                >
                  −
                </button>
                <span className="font-display text-3xl w-10 text-center">{adults}</span>
                <button
                  type="button"
                  onClick={() => setAdults(Math.min(8, adults + 1))}
                  className="h-10 w-10 border border-border text-lg"
                >
                  +
                </button>
                <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{m.adults}</span>
              </div>
              {extraPersons > 0 && (
                <p className="mt-4 text-xs text-gold">{m.extraNote(extraPersons, extraPersonsCost)}</p>
              )}
            </div>
          )}

          {step === 4 && (
            <div>
              <h3 className="font-display text-2xl">{m.s4title}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{m.s4sub}</p>
              <div className="mt-6 grid gap-3">
                {UPSELLS.map((up) => {
                  const cost = up.perPerson ? up.price * adults : up.price;
                  const checked = !!picks[up.id];
                  return (
                    <label
                      key={up.id}
                      className={`flex cursor-pointer items-center justify-between border p-4 transition ${
                        checked ? "border-gold bg-gold/5" : "border-border"
                      }`}
                    >
                      <span className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => setPicks((p) => ({ ...p, [up.id]: e.target.checked }))}
                          className="accent-gold"
                        />
                        <span className="text-sm">{m.upsellLabels[up.id]}</span>
                      </span>
                      <span className="text-xs uppercase tracking-[0.18em] text-gold">{cost} €</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {step === 5 && !sent && (
            <div>
              <h3 className="font-display text-2xl">{m.s5title}</h3>
              <div className="mt-5 border border-border divide-y divide-border text-sm">
                <Row label={m.unit}>{uName}</Row>
                <Row label={m.stay}>
                  {nights} {m.nightsWord} · {arrival} → {departure}
                </Row>
                <Row label={m.travelers}>{adults}</Row>
                <Row label={m.lodging(nightly, nights)}>{nightly * nights} €</Row>
                {extraPersonsCost > 0 && <Row label={m.extraPersons}>{extraPersonsCost} €</Row>}
                {upsellTotal > 0 && <Row label={m.extras}>{upsellTotal} €</Row>}
                <Row label={m.tax}>{taxeSejour.toFixed(2)} €</Row>
                <div className="flex items-baseline justify-between bg-gold/10 p-4">
                  <span className="text-[11px] uppercase tracking-[0.22em] text-gold">{m.total}</span>
                  <span className="font-display text-2xl">{grandTotal.toFixed(2)} €</span>
                </div>
              </div>

              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                <FormText
                  label={m.firstname}
                  value={contact.prenom}
                  error={errors.prenom}
                  onChange={(v) => setContact({ ...contact, prenom: v })}
                />
                <FormText label={m.lastname} value={contact.nom} error={errors.nom} onChange={(v) => setContact({ ...contact, nom: v })} />
                <FormText
                  label={m.email}
                  type="email"
                  value={contact.email}
                  error={errors.email}
                  onChange={(v) => setContact({ ...contact, email: v })}
                />
                <FormText
                  label={m.phone}
                  type="tel"
                  value={contact.telephone}
                  error={errors.telephone}
                  onChange={(v) => setContact({ ...contact, telephone: v })}
                />
                <div className="sm:col-span-2">
                  <label className="block text-[10px] uppercase tracking-[0.22em] text-muted-foreground">{m.message}</label>
                  <textarea
                    value={contact.message}
                    maxLength={500}
                    onChange={(e) => setContact({ ...contact, message: e.target.value })}
                    rows={3}
                    className="mt-1 w-full border border-border bg-background p-3 text-sm outline-none focus:border-gold"
                  />
                </div>
                <a
                  href={whatsappHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={validateAndSend}
                  data-testid="booking-modal-submit-whatsapp"
                  className="sm:col-span-2 mt-2 bg-gold py-4 text-[11px] uppercase tracking-[0.25em] text-primary-foreground text-center transition hover:brightness-95 cursor-pointer"
                >
                  {m.submit}
                </a>
                <p className="sm:col-span-2 text-[10px] text-muted-foreground text-center">{m.note}</p>
              </div>
            </div>
          )}

          {sent && (
            <div className="flex flex-col items-center gap-4 py-12 text-center">
              <div className="hairline w-16" />
              <p className="font-display text-3xl italic text-gold">{m.sentTitle}</p>
              <p className="max-w-sm text-sm text-muted-foreground">{m.sentSub}</p>
              <button
                onClick={onClose}
                className="mt-2 border border-foreground/30 px-6 py-3 text-[11px] uppercase tracking-[0.22em] hover:border-gold hover:text-gold"
              >
                {m.close}
              </button>
            </div>
          )}
        </div>

        {!sent && step < 5 && (
          <div className="flex items-center justify-between border-t border-border px-6 py-4">
            <button
              type="button"
              onClick={prev}
              disabled={step === 1}
              className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground disabled:opacity-30 hover:text-foreground"
            >
              {m.back}
            </button>
            <button
              type="button"
              onClick={next}
              disabled={step === 2 && nights <= 0}
              className="bg-foreground px-6 py-3 text-[11px] uppercase tracking-[0.22em] text-background transition hover:bg-gold hover:text-primary-foreground disabled:opacity-40"
            >
              {m.next}
            </button>
          </div>
        )}
        {!sent && step === 5 && (
          <div className="flex items-center justify-between border-t border-border px-6 py-4">
            <button
              type="button"
              onClick={prev}
              className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground hover:text-foreground"
            >
              {m.back}
            </button>
            <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">{m.finalStep}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-center justify-between p-3">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm">{children}</span>
    </div>
  );
}

function FieldDate({ label, value, onChange, min }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.22em] text-muted-foreground">{label}</span>
      <input
        type="date"
        value={value}
        min={min || new Date().toISOString().split("T")[0]}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full border border-border bg-background p-3 text-sm outline-none focus:border-gold"
      />
    </label>
  );
}

function FormText({ label, value, onChange, type = "text", error }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.22em] text-muted-foreground">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`mt-1 w-full border bg-background p-3 text-sm outline-none focus:border-gold ${
          error ? "border-destructive" : "border-border"
        }`}
      />
      {error && <span className="mt-1 block text-[10px] text-destructive">{error}</span>}
    </label>
  );
}
